import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { CONSENT_COOKIE_NAME } from '@/lib/i18n';

interface AnalyticsConsentState {
  canTrack: boolean;
  loading: boolean;
  cookieConsentGiven: boolean;
  userObjectsToAnalytics: boolean;
}

/**
 * Hook to check if analytics tracking is allowed.
 * Combines localStorage cookie consent with database user preferences.
 * 
 * GDPR Compliance:
 * - Art. 7.3: Respects withdrawal of consent (cookie settings)
 * - Art. 21: Respects right to object (user preferences)
 */
export function useAnalyticsConsent(): AnalyticsConsentState {
  const { user } = useAuth();
  const [state, setState] = useState<AnalyticsConsentState>({
    canTrack: false,
    loading: true,
    cookieConsentGiven: false,
    userObjectsToAnalytics: false,
  });

  const checkConsent = useCallback(async () => {
    // Check localStorage for cookie consent using the correct key
    const cookieConsent = localStorage.getItem(CONSENT_COOKIE_NAME);
    let cookieConsentGiven = false;
    
    if (cookieConsent) {
      try {
        const parsed = JSON.parse(cookieConsent);
        // User must have accepted analytics cookies
        cookieConsentGiven = parsed.analytics === true;
      } catch {
        cookieConsentGiven = false;
      }
    }

    // For unauthenticated users, only check cookie consent
    if (!user?.id) {
      setState({
        canTrack: cookieConsentGiven,
        loading: false,
        cookieConsentGiven,
        userObjectsToAnalytics: false,
      });
      return;
    }

    // For authenticated users, also check database preferences
    try {
      const { data, error } = await supabase
        .from('user_preferences')
        .select('object_to_analytics')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) {
        console.error('Error fetching analytics preferences:', error);
      }

      const userObjectsToAnalytics = data?.object_to_analytics ?? false;

      // Can track only if: cookie consent given AND user hasn't objected
      setState({
        canTrack: cookieConsentGiven && !userObjectsToAnalytics,
        loading: false,
        cookieConsentGiven,
        userObjectsToAnalytics,
      });
    } catch (error) {
      console.error('Error checking analytics consent:', error);
      // On error, default to not tracking (safe choice)
      setState({
        canTrack: false,
        loading: false,
        cookieConsentGiven,
        userObjectsToAnalytics: false,
      });
    }
  }, [user?.id]);

  useEffect(() => {
    checkConsent();

    // Listen for cookie consent changes
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === CONSENT_COOKIE_NAME) {
        checkConsent();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [checkConsent]);

  return state;
}

/**
 * Utility function to check cookie consent without React hooks.
 * For use in non-React contexts like trackEvents.ts
 */
export function checkCookieConsent(): boolean {
  if (typeof window === 'undefined') return false;
  
  const cookieConsent = localStorage.getItem(CONSENT_COOKIE_NAME);
  if (!cookieConsent) return false;
  
  try {
    const parsed = JSON.parse(cookieConsent);
    return parsed.analytics === true;
  } catch {
    return false;
  }
}

/**
 * Check if a user has objected to analytics in their preferences.
 * For use in non-React contexts.
 */
export async function checkUserAnalyticsObjection(userId: string): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .from('user_preferences')
      .select('object_to_analytics')
      .eq('user_id', userId)
      .maybeSingle();

    if (error) {
      console.error('Error checking analytics objection:', error);
      return false;
    }

    return data?.object_to_analytics ?? false;
  } catch (error) {
    console.error('Error checking analytics objection:', error);
    return false;
  }
}

